<template>
  <div class="error-handling-example">
    <el-card>
      <div slot="header">
        <span>Error Handling System Example</span>
      </div>

      <el-alert
        title="Error Handling Features"
        type="info"
        description="This example demonstrates the comprehensive error handling system with beautiful UI, retry mechanisms, and user-friendly messages."
        show-icon
        :closable="false"
        style="margin-bottom: 20px;">
      </el-alert>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-card>
            <div slot="header">
              <span>API Operations</span>
            </div>

            <el-form ref="exampleForm" :model="form" :rules="rules" label-width="120px">
              <el-form-item label="Name" prop="name">
                <el-input v-model="form.name" placeholder="Enter name"></el-input>
              </el-form-item>

              <el-form-item label="Email" prop="email">
                <el-input v-model="form.email" placeholder="Enter email"></el-input>
              </el-form-item>

              <el-form-item>
                <el-button type="primary" @click="submitForm" :loading="loading">
                  Submit Form
                </el-button>
                <el-button @click="testApiCall" :loading="apiLoading">
                  Test API Call
                </el-button>
              </el-form-item>
            </el-form>
          </el-card>
        </el-col>

        <el-col :span="12">
          <el-card>
            <div slot="header">
              <span>Error Scenarios</span>
            </div>

            <el-button @click="simulateNetworkError" type="warning" block style="margin-bottom: 10px;">
              Simulate Network Error
            </el-button>

            <el-button @click="simulateValidationError" type="danger" block style="margin-bottom: 10px;">
              Simulate Validation Error
            </el-button>

            <el-button @click="simulateServerError" type="danger" block style="margin-bottom: 10px;">
              Simulate Server Error
            </el-button>

            <el-button @click="simulateSuccess" type="success" block style="margin-bottom: 10px;">
              Simulate Success
            </el-button>
          </el-card>
        </el-col>
      </el-row>

      <el-row style="margin-top: 20px;">
        <el-col :span="24">
          <el-card>
            <div slot="header">
              <span>Results</span>
            </div>

            <div v-if="results.length === 0" class="no-results">
              <el-empty description="No results yet. Try the operations above to see error handling in action."></el-empty>
            </div>

            <div v-else>
              <el-timeline>
                <el-timeline-item
                  v-for="(result, index) in results"
                  :key="index"
                  :type="result.type"
                  :color="getTimelineColor(result.type)"
                  :timestamp="result.timestamp">
                  <h4>{{ result.title }}</h4>
                  <p>{{ result.message }}</p>
                </el-timeline-item>
              </el-timeline>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'ErrorHandlingExample',
  data () {
    return {
      loading: false,
      apiLoading: false,
      form: {
        name: '',
        email: ''
      },
      rules: {
        name: [
          { required: true, message: 'Name is required', trigger: 'blur' }
        ],
        email: [
          { required: true, message: 'Email is required', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email', trigger: 'blur' }
        ]
      },
      results: []
    }
  },
  methods: {
    // Submit form with error handling
    async submitForm () {
      try {
        await this.$refs.exampleForm.validate()

        await this.withLoading(async () => {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000))

          // Simulate random success/failure
          if (Math.random() > 0.7) {
            throw new Error('Simulated form submission error')
          }

          this.addResult('success', 'Form Submitted', 'Form submitted successfully!')
          this.showSuccess('Form submitted successfully!')
        }, 'loading', {
          component: 'ErrorHandlingExample',
          action: 'submitForm',
          formData: this.form
        })
      } catch (error) {
        // Error is handled by withLoading
        this.addResult('error', 'Form Error', error.message)
      }
    },

    // Test API call with retry
    async testApiCall () {
      try {
        await this.withLoading(async () => {
          // Simulate API call that might fail
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              if (Math.random() > 0.5) {
                reject(new Error('API call failed'))
              } else {
                resolve()
              }
            }, 1500)
          })

          this.addResult('success', 'API Call', 'API call completed successfully!')
          this.showSuccess('API call completed successfully!')
        }, 'apiLoading', {
          component: 'ErrorHandlingExample',
          action: 'testApiCall'
        })
      } catch (error) {
        this.addResult('error', 'API Error', error.message)
      }
    },

    // Simulate network error
    simulateNetworkError () {
      const error = new Error('Network Error')
      error.message = 'Network Error'

      this.handleError(error, {
        component: 'ErrorHandlingExample',
        action: 'simulateNetworkError'
      })

      this.addResult('error', 'Network Error', 'Simulated network connection error')
    },

    // Simulate validation error
    simulateValidationError () {
      const error = new Error('Validation failed')
      error.response = {
        status: 422,
        data: {
          name: ['The name field is required.'],
          email: ['The email field must be a valid email address.']
        }
      }

      this.handleError(error, {
        component: 'ErrorHandlingExample',
        action: 'simulateValidationError'
      })

      this.addResult('error', 'Validation Error', 'Simulated form validation error')
    },

    // Simulate server error
    simulateServerError () {
      const error = new Error('Server error')
      error.response = {
        status: 500,
        data: {
          message: 'An unexpected error occurred on the server.'
        }
      }

      this.handleError(error, {
        component: 'ErrorHandlingExample',
        action: 'simulateServerError'
      })

      this.addResult('error', 'Server Error', 'Simulated server error')
    },

    // Simulate success
    simulateSuccess () {
      this.showSuccess('This is a test success message!')
      this.addResult('success', 'Success', 'Simulated success message')
    },

    // Add result to timeline
    addResult (type, title, message) {
      this.results.unshift({
        type,
        title,
        message,
        timestamp: new Date().toLocaleTimeString()
      })
    },

    // Get timeline color based on type
    getTimelineColor (type) {
      switch (type) {
        case 'success':
          return '#67C23A'
        case 'error':
          return '#F56C6C'
        case 'warning':
          return '#E6A23C'
        default:
          return '#909399'
      }
    }
  }
}
</script>

<style scoped>
.error-handling-example {
  padding: 20px;
}

.no-results {
  text-align: center;
  padding: 40px 0;
}

.el-card {
  margin-bottom: 20px;
}

.el-button {
  margin-bottom: 10px;
}
</style>
